import React, { useRef, useState, useMemo } from 'react';
import { useScroll } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { easing } from 'maath';
import * as THREE from 'three';
import Cabin from './Cabin';
import useStore from '../../store';

import { CATEGORIES } from '../../data/categories';

// --- SHARED RESOURCES (Optimization) ---
// Unit cylinder: Radius 0.5, Height 1.
const BEAM_GEO = new THREE.CylinderGeometry(0.5, 0.5, 1, 8);
BEAM_GEO.rotateX(Math.PI / 2);
const BEAM_MAT = new THREE.MeshStandardMaterial({ color: "#222", metalness: 0.6, roughness: 0.3 });

const Beam = ({ start, end, thickness = 0.15, color = null }) => {
    const s = useMemo(() => new THREE.Vector3(...start), [start]);
    const e = useMemo(() => new THREE.Vector3(...end), [end]);
    const { mid, length, rotation } = useMemo(() => {
        const dist = s.distanceTo(e);
        const m = s.clone().add(e).multiplyScalar(0.5);
        const obj = new THREE.Object3D();
        obj.position.copy(m);
        obj.lookAt(e);
        return { mid: m, length: dist, rotation: obj.rotation };
    }, [s, e]);

    return (
        <group position={mid} rotation={rotation}>
            <mesh
                geometry={BEAM_GEO}
                material={BEAM_MAT}
                scale={[thickness, thickness, length]}
            />
        </group>
    )
}

// Neon Palette for the Chaser Effect
const NEON_PALETTE = [
    new THREE.Color('#ff00ff'),
    new THREE.Color('#00ffff'),
    new THREE.Color('#39ff14'),
    new THREE.Color('#ffff00'),
    new THREE.Color('#ff0055'),
];
const COLOR_DIM = new THREE.Color('#000000'); // Pure Black

// --- SHARED GEOMETRIES (Optimization continued) ---
const GEO_WHEEL = {
    rimOuter: new THREE.TorusGeometry(6, 0.1, 8, 64),
    rimInner: new THREE.TorusGeometry(4.5, 0.08, 8, 64),
    bracing: new THREE.BoxGeometry(1.5, 0.05, 0.05),
    spoke: new THREE.CylinderGeometry(0.05, 0.15, 4.5, 8),
    hubMain: new THREE.CylinderGeometry(0.8, 0.8, 0.3, 32),
    hubRing: new THREE.TorusGeometry(0.6, 0.05, 8, 32),
    axle: new THREE.CylinderGeometry(0.3, 0.3, 3),
};

// Rotate hub main once
GEO_WHEEL.hubMain.rotateX(Math.PI / 2);

// --- SHARED MATERIALS ---
const MAT_WHEEL = {
    darkMetal: new THREE.MeshStandardMaterial({ color: "#222", metalness: 0.8, roughness: 0.2 }),
    bracing: new THREE.MeshStandardMaterial({ color: "#333" }),
    spoke: new THREE.MeshStandardMaterial({ color: "#444", metalness: 0.8, roughness: 0.5 }),
    hub: new THREE.MeshStandardMaterial({ color: "#111", metalness: 0.9, roughness: 0.2 }),
    hubRing: new THREE.MeshStandardMaterial({ color: "#555" }),
    axle: new THREE.MeshStandardMaterial({ color: "#444" }),
};


const Wheel = () => {
    const wheelRef = useRef();
    const scroll = useScroll();
    const [lastActiveIndex, setLastActiveIndex] = useState(-1);

    // Zustand Store
    const active = useStore((state) => state.stage === 'wheel');
    const setActiveCategory = useStore((state) => state.setActiveCategory);
    const setZoomedCategoryId = useStore((state) => state.setZoomedCategoryId);
    const isWheelLocked = useStore((state) => state.isWheelLocked);
    const setWheelLocked = useStore((state) => state.setWheelLocked);

    // Instanced Mesh for carnival lights (Bulbs) on the Truss Rim
    const bulbCount = 48;
    const pinkBulbsRef = useRef();
    const cyanBulbsRef = useRef();

    const { pinkData, cyanData } = useMemo(() => {
        const pData = [];
        const cData = [];
        const radius = 6;
        for (let i = 0; i < bulbCount; i++) {
            const angle = (i / bulbCount) * Math.PI * 2;
            const isPink = i % 2 === 0;
            // Place lights on the OUTER ring of the truss
            const posFront = { x: Math.sin(angle) * radius, y: Math.cos(angle) * radius, z: 0.5 };
            const posBack = { x: Math.sin(angle) * radius, y: Math.cos(angle) * radius, z: -0.5 };

            if (isPink) {
                pData.push(posFront, posBack);
            } else {
                cData.push(posFront, posBack);
            }
        }
        return { pinkData: pData, cyanData: cData };
    }, []);

    useFrame((state, delta) => {
        if (!wheelRef.current) return;

        // Skip rotation updates if wheel is locked (Zooming)
        if (!isWheelLocked) {
            // 1. Rotation Physics
            const scrollOffset = scroll.offset;
            const wheelProgress = Math.max(0, (scrollOffset - 0.2) * 1.25);
            const targetRotation = -wheelProgress * Math.PI * 4;
            easing.dampE(wheelRef.current.rotation, [0, 0, targetRotation], 0.3, delta);
        }

        // 2. Active Zone Logic
        let closestIndex = -1;
        let minDiff = 0.8;
        const targetGap = Math.PI;

        CATEGORIES.forEach((cat, i) => {
            const baseAngle = (i / CATEGORIES.length) * Math.PI * 2;
            let currentAngle = (baseAngle + wheelRef.current.rotation.z) % (Math.PI * 2);
            if (currentAngle < 0) currentAngle += Math.PI * 2;
            let diff = Math.abs(currentAngle - targetGap);
            if (diff < minDiff) {
                closestIndex = (CATEGORIES.length - 1) - i;
                minDiff = diff;
            }
        });

        if (closestIndex !== lastActiveIndex) {
            setLastActiveIndex(closestIndex);
            if (active && closestIndex !== -1) {
                setActiveCategory(CATEGORIES[closestIndex]);
            } else {
                setActiveCategory(null);
            }
        }

        // 3. Update Bulb Instanced Meshes (Neon Chaser Logic)
        const time = state.clock.getElapsedTime();
        const tempObj = new THREE.Object3D();

        const updateLights = (ref, positions, offsetIndex = 0) => {
            if (ref.current) {
                positions.forEach((pos, i) => {
                    tempObj.position.set(pos.x, pos.y, pos.z);
                    const scale = 1 + Math.sin(time * 5 + i) * 0.1;
                    tempObj.scale.set(scale, scale, scale);
                    tempObj.updateMatrix();
                    ref.current.setMatrixAt(i, tempObj.matrix);

                    // Dynamic Neon Color Logic
                    const shift = (i + offsetIndex) + (time * 8);
                    const colorIndex = Math.floor(Math.abs(shift * 0.2)) % NEON_PALETTE.length;
                    const blink = Math.sin(shift * 0.8);
                    const color = blink > 0.2 ? NEON_PALETTE[colorIndex] : COLOR_DIM;
                    ref.current.setColorAt(i, color);
                });
                ref.current.instanceMatrix.needsUpdate = true;
                ref.current.instanceColor.needsUpdate = true;
            }
        };

        updateLights(pinkBulbsRef, pinkData, 0);
        updateLights(cyanBulbsRef, cyanData, 24);

    });

    const radius = 6;
    const innerRadius = 4.5;

    const handleCabinClick = (category, index) => {
        // 1. Zoom In Immediately
        setZoomedCategoryId(category.id);
        setWheelLocked(false);

        // 2. Scroll/Rotate Wheel to bring cabin to Left (Active Position)
        if (scroll && scroll.el) {
            const baseAngle = (index / CATEGORIES.length) * Math.PI * 2;
            let targetRotZ = Math.PI - baseAngle;

            while (targetRotZ > 0) targetRotZ -= Math.PI * 2;
            while (targetRotZ < -Math.PI * 4) targetRotZ += Math.PI * 2;

            let targetOffset = 0.2 - (targetRotZ / (5 * Math.PI));

            targetOffset = Math.max(0, Math.min(1, targetOffset));
            const targetTop = targetOffset * (scroll.el.scrollHeight - scroll.el.clientHeight);
            scroll.el.scrollTo({ top: targetTop, behavior: 'smooth' });
        }
    };

    // A-FRAME GEOMETRY CALCULATION
    const standHeight = 9;
    const groundY = -(radius + 2.5);
    const spreadX = 5;
    const spreadZFront = 3.5;
    const spreadZBack = -3.5;
    const axleZFront = 1.2;
    const axleZBack = -1.2;

    return (
        <group position={[5, 0, 0]} visible={!active && !isWheelLocked ? false : true}>
            {/* Rotating Wheel Group */}
            <group ref={wheelRef}>

                {/* --- 1. RIMS (TRUSS STRUCTURE) --- */}
                <mesh position={[0, 0, -0.5]} geometry={GEO_WHEEL.rimOuter} material={MAT_WHEEL.darkMetal} />
                <mesh position={[0, 0, 0.5]} geometry={GEO_WHEEL.rimOuter} material={MAT_WHEEL.darkMetal} />
                <mesh position={[0, 0, -0.5]} geometry={GEO_WHEEL.rimInner} material={MAT_WHEEL.darkMetal} />
                <mesh position={[0, 0, 0.5]} geometry={GEO_WHEEL.rimInner} material={MAT_WHEEL.darkMetal} />

                {/* Bracing */}
                {Array.from({ length: 24 }).map((_, i) => {
                    const angle = (i / 24) * Math.PI * 2;
                    return (
                        <group key={i} rotation={[0, 0, angle]}>
                            <mesh
                                position={[radius - (radius - innerRadius) / 2, 0, -0.5]}
                                geometry={GEO_WHEEL.bracing}
                                material={MAT_WHEEL.bracing}
                            />
                            <mesh
                                position={[radius - (radius - innerRadius) / 2, 0, 0.5]}
                                geometry={GEO_WHEEL.bracing}
                                material={MAT_WHEEL.bracing}
                            />
                        </group>
                    )
                })}

                {/* --- 2. SPOKES --- */}
                {CATEGORIES.map((cat, i) => {
                    const angle = (i / CATEGORIES.length) * Math.PI * 2;
                    return (
                        <group key={`spoke-${i}`} rotation={[0, 0, angle]}>
                            <mesh
                                position={[innerRadius / 2, 0, 0]}
                                rotation={[0, 0, -Math.PI / 2]}
                                geometry={GEO_WHEEL.spoke}
                                material={MAT_WHEEL.spoke}
                            />
                        </group>
                    )
                })}

                {/* --- 3. CENTER HUB --- */}
                <group position={[0, 0, 0]}>
                    <mesh geometry={GEO_WHEEL.hubMain} material={MAT_WHEEL.hub} />
                    <mesh position={[0, 0, 0.16]} geometry={GEO_WHEEL.hubRing} material={MAT_WHEEL.hubRing} />
                    <mesh position={[0, 0, -0.16]} geometry={GEO_WHEEL.hubRing} material={MAT_WHEEL.hubRing} />
                </group>

                {/* --- 4. LIGHTS --- */}
                <instancedMesh ref={pinkBulbsRef} args={[null, null, pinkData.length]}>
                    <sphereGeometry args={[0.12, 9, 9]} />
                    <meshBasicMaterial toneMapped={false} />
                </instancedMesh>
                <instancedMesh ref={cyanBulbsRef} args={[null, null, cyanData.length]}>
                    <sphereGeometry args={[0.12, 8, 8]} />
                    <meshBasicMaterial toneMapped={false} />
                </instancedMesh>

                {/* --- 5. CABINS --- */}
                {CATEGORIES.map((category, i) => {
                    const angle = (i / CATEGORIES.length) * Math.PI * 2;
                    const x = Math.sin(angle) * radius;
                    const y = Math.cos(angle) * radius;
                    return (
                        <Cabin
                            key={category.id}
                            position={[x, y, 0]}
                            category={category}
                            isActive={i === lastActiveIndex}
                            wheelRotationRef={wheelRef}
                            onClick={() => handleCabinClick(category, i)}
                            isLocked={isWheelLocked && lastActiveIndex === i}
                        />
                    );
                })}
            </group>

            {/* --- 6. A-FRAME STAND --- */}
            <group>
                {/* Front A-Frame */}
                <Beam start={[0, 0, axleZFront]} end={[-spreadX, groundY, spreadZFront]} thickness={0.3} />
                <Beam start={[0, 0, axleZFront]} end={[spreadX, groundY, spreadZFront]} thickness={0.3} />
                {/* Front Bracing */}
                <Beam start={[-spreadX / 2, groundY / 2, spreadZFront * 0.75 + axleZFront * 0.25]} end={[spreadX / 2, groundY / 2, spreadZFront * 0.75 + axleZFront * 0.25]} thickness={0.15} />

                {/* Back A-Frame */}
                <Beam start={[0, 0, axleZBack]} end={[-spreadX, groundY, spreadZBack]} thickness={0.3} />
                <Beam start={[0, 0, axleZBack]} end={[spreadX, groundY, spreadZBack]} thickness={0.3} />
                {/* Back Bracing */}
                <Beam start={[-spreadX / 2, groundY / 2, spreadZBack * 0.75 + axleZBack * 0.25]} end={[spreadX / 2, groundY / 2, spreadZBack * 0.75 + axleZBack * 0.25]} thickness={0.15} />

                {/* Central Axle Bar */}
                <mesh rotation={[Math.PI / 2, 0, 0]} geometry={GEO_WHEEL.axle} material={MAT_WHEEL.axle} />
            </group>
        </group>
    );
};

export default Wheel;
